import { localizeAssetFieldLabel } from './assetFieldLabels';
import type { TranslateFn } from './operationLabels';

function normalizeServerMessage(message: string): string {
  return message.trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizeFieldKey(key: string): string {
  return key.trim().toLowerCase().replace(/\s+/g, '_');
}

function isGenericBadRequestMessage(message: string): boolean {
  const normalized = normalizeServerMessage(message);
  return (
    normalized === 'bad request' ||
    normalized === 'bad request.' ||
    normalized === 'validation failed' ||
    normalized === 'unprocessable entity'
  );
}

function pushMessage(out: string[], value: unknown): void {
  if (typeof value === 'string' && value.trim()) {
    out.push(value.trim());
  }
}

function parseFieldConstraintLine(line: string): { field: string; constraint: string } | null {
  const trimmed = line.trim();
  const colonMatch = trimmed.match(/^([\w.]+)\s*:\s*(.+)$/);
  if (colonMatch) {
    return { field: colonMatch[1]!, constraint: colonMatch[2]!.trim() };
  }
  const spaceMatch = trimmed.match(/^([\w.]+)\s+(.+)$/);
  if (spaceMatch) {
    return { field: spaceMatch[1]!, constraint: spaceMatch[2]!.trim() };
  }
  return null;
}

/** Pull user-facing lines from Nest/Express JSON error bodies (string, array, or field map). */
export function extractResponseErrorMessages(responseText: string): string[] {
  const trimmed = responseText.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    const out: string[] = [];

    const message = parsed.message;
    if (Array.isArray(message)) {
      for (const item of message) {
        pushMessage(out, item);
      }
    } else if (typeof message === 'string') {
      pushMessage(out, message);
    } else if (message && typeof message === 'object' && !Array.isArray(message)) {
      for (const [field, value] of Object.entries(message as Record<string, unknown>)) {
        if (typeof value === 'string') {
          out.push(`${field}: ${value}`);
        } else if (Array.isArray(value)) {
          for (const item of value) {
            if (typeof item === 'string') {
              out.push(`${field}: ${item}`);
            }
          }
        }
      }
    }

    pushMessage(out, parsed.error);
    pushMessage(out, parsed.description);
    pushMessage(out, parsed.detail);
    pushMessage(out, parsed.title);

    if (parsed.error && typeof parsed.error === 'object' && !Array.isArray(parsed.error)) {
      const inner = parsed.error as Record<string, unknown>;
      pushMessage(out, inner.message);
      pushMessage(out, inner.description);
      pushMessage(out, inner.detail);
    }

    return out.filter((line, index, all) => all.indexOf(line) === index);
  } catch {
    const flat = trimmed.replace(/\s+/g, ' ').trim();
    if (flat && !trimmed.startsWith('<') && flat.length <= 240) {
      return [flat];
    }
    return [];
  }
}

function isUserDoesNotExistServerMessage(normalized: string): boolean {
  return (
    /user does not exist/.test(normalized) ||
    /user doesn't exist/.test(normalized) ||
    /user does not exists/.test(normalized)
  );
}

/** Map known API English errors to localized user-facing text. */
export function localizeServerMessage(message: string, t: TranslateFn): string {
  const normalized = normalizeServerMessage(message);
  if (
    normalized.includes('user with this email or phone number already exists') ||
    normalized.includes('a user with the same phone number or email already exists') ||
    normalized.includes('user with this email or phone already exists')
  ) {
    return t('register.error.userAlreadyExists');
  }
  if (isUserDoesNotExistServerMessage(normalized)) {
    return t('auth.error.userDoesNotExist');
  }
  if (isGenericBadRequestMessage(message)) {
    return t('register.error.badRequest');
  }
  if (normalized === 'conflict' || normalized === 'conflict.') {
    return t('register.error.conflict');
  }
  if (normalized === 'unauthorized' || normalized === 'forbidden') {
    return t('register.error.forbidden');
  }
  if (
    normalized.includes('complete registration before booking') ||
    normalized.includes('complete your registration before booking') ||
    (normalized.includes('complete registration') && normalized.includes('book'))
  ) {
    return t('bookings.error.completeRegistration');
  }
  return message;
}

const OTP_FAILURE_EXACT: Array<{ match: string; key: Parameters<TranslateFn>[0] }> = [
  { match: 'could not send a sign-in code.', key: 'auth.portal.otpSendFailed' },
  { match: 'could not verify the sign-in code.', key: 'auth.portal.otpVerifyFailed' },
  {
    match:
      'sign-in was rejected. check your identifier and password, or account access.',
    key: 'auth.portal.otpSignInRejected',
  },
  {
    match: 'the login address could not be reached. check the api configuration.',
    key: 'auth.portal.otpLoginNotFound',
  },
  {
    match: 'the server could not complete sign-in. try again later.',
    key: 'auth.portal.otpServerError',
  },
  {
    match: 'sign-in could not be completed. check the information you entered.',
    key: 'auth.portal.otpRequestFailed',
  },
  { match: 'sign-in failed.', key: 'auth.portal.otpSignInFailed' },
  {
    match: 'could not reach the server. check your connection, proxy, or cors settings.',
    key: 'auth.portal.otpNetworkUnreachable',
  },
  {
    match: 'something went wrong while contacting the server.',
    key: 'auth.portal.otpNetworkError',
  },
];

/** Map OTP send/verify English fallbacks and known API errors to localized UI text. */
export function localizeOtpFailureMessage(
  message: string,
  t: TranslateFn,
  channel: 'email' | 'phone' = 'email',
): string {
  const known = localizeServerMessage(message, t);
  if (known !== message) {
    return known;
  }

  const normalized = normalizeServerMessage(message);
  for (const entry of OTP_FAILURE_EXACT) {
    if (normalized === entry.match) {
      return t(entry.key);
    }
  }

  if (
    /invalid.*code|incorrect.*code|wrong.*code|bad.*code|code.*invalid|code.*incorrect|code.*mismatch/.test(
      normalized,
    )
  ) {
    return t('auth.portal.otpInvalidCode');
  }
  if (/expired|expiration|no longer valid|past due/.test(normalized)) {
    return t('auth.portal.otpExpired');
  }
  if (/too many|rate limit|max attempts|attempts exceeded|try again later/.test(normalized)) {
    return t('auth.portal.otpTooManyAttempts');
  }
  if (/user does not exist|user not found|account not found|not registered/.test(normalized)) {
    return channel === 'phone'
      ? t('auth.portal.phoneOtpUserNotRegistered')
      : t('auth.portal.otpUserNotRegistered');
  }

  return message;
}

const PASSWORD_RESET_FAILURE_EXACT: Array<{ match: string; key: Parameters<TranslateFn>[0] }> = [
  { match: 'could not send a reset code.', key: 'auth.forgotPassword.sendFailed' },
  { match: 'could not verify the reset code.', key: 'auth.forgotPassword.verifyFailed' },
  {
    match: 'could not update the password. try again.',
    key: 'auth.forgotPassword.completeFailed',
  },
  {
    match: 'the reset code was accepted but no reset token was returned.',
    key: 'auth.forgotPassword.noResetToken',
  },
];

/** Map password-reset send/verify/complete errors. Does not reuse lite-register OTP copy. */
export function localizePasswordResetFailureMessage(
  message: string,
  t: TranslateFn,
  channel: 'email' | 'phone' = 'email',
): string {
  const normalized = normalizeServerMessage(message);
  if (
    /user does not exist|user doesn't exist|user does not exists|user not found|account not found|not registered/.test(
      normalized,
    )
  ) {
    return channel === 'phone'
      ? t('auth.forgotPassword.phoneUserNotFound')
      : t('auth.forgotPassword.userNotFound');
  }

  const known = localizeServerMessage(message, t);
  if (known !== message) {
    return known;
  }
  for (const entry of PASSWORD_RESET_FAILURE_EXACT) {
    if (normalized === entry.match) {
      return t(entry.key);
    }
  }

  if (
    /does not (use|have) a password|no password|passwordless|sign in with google/.test(normalized)
  ) {
    return t('auth.forgotPassword.noPasswordAccount');
  }
  if (
    /invalid.*code|incorrect.*code|wrong.*code|bad.*code|code.*invalid|code.*incorrect|code.*mismatch/.test(
      normalized,
    )
  ) {
    return t('auth.portal.otpInvalidCode');
  }
  if (/expired|expiration|no longer valid|past due/.test(normalized)) {
    return t('auth.forgotPassword.tokenExpired');
  }
  if (/too many|rate limit|max attempts|attempts exceeded|try again later/.test(normalized)) {
    return t('auth.portal.otpTooManyAttempts');
  }

  return message;
}

function localizeConstraint(fieldLabel: string, constraint: string, t: TranslateFn): string | null {
  const c = constraint.toLowerCase();
  if (
    c.includes('should not be empty') ||
    c.includes('must not be empty') ||
    c.includes('should not be null') ||
    c.includes('must not be null')
  ) {
    return t('register.error.fieldRequired', { field: fieldLabel });
  }
  if (c.includes('must be an email') || c.includes('must be a valid email')) {
    return t('register.error.fieldInvalidEmail', { field: fieldLabel });
  }
  if (c.includes('phone')) {
    return t('register.error.fieldInvalidPhone', { field: fieldLabel });
  }
  if (c.includes('must be a string')) {
    return t('register.error.fieldInvalidString', { field: fieldLabel });
  }
  if (c.includes('must be a number') || c.includes('must be an integer')) {
    return t('register.error.fieldInvalidNumber', { field: fieldLabel });
  }
  if (c.includes('already exists') || c.includes('already in use')) {
    return t('register.error.userAlreadyExists');
  }
  return null;
}

/** Translate Nest/class-validator style field errors using the active UI language. */
export function localizeValidationMessage(message: string, t: TranslateFn): string {
  const known = localizeServerMessage(message, t);
  if (known !== message) {
    return known;
  }

  const parsed = parseFieldConstraintLine(message);
  if (parsed) {
    const fieldLabel = localizeAssetFieldLabel(normalizeFieldKey(parsed.field), t);
    const localizedConstraint = localizeConstraint(fieldLabel, parsed.constraint, t);
    if (localizedConstraint) {
      return localizedConstraint;
    }
    return t('register.error.fieldConstraint', {
      field: fieldLabel,
      detail: parsed.constraint,
    });
  }

  return message;
}

function joinLocalizedMessages(messages: string[], t: TranslateFn): string {
  const localized = messages
    .map((line) => localizeValidationMessage(line, t))
    .filter((line, index, all) => line.trim() && all.indexOf(line) === index);
  return localized.join(' · ');
}

export type RegisterSubmittedContactIds = {
  email: string;
  phone: string;
  businessId: string;
};

export function pickRegisterSubmittedContactIds(
  body: Record<string, unknown>,
): RegisterSubmittedContactIds {
  const read = (key: string) => (typeof body[key] === 'string' ? body[key].trim() : '');
  return {
    email: read('email'),
    phone: read('phone_number'),
    businessId: read('business_id'),
  };
}

export function isRegisterUserAlreadyExistsResponse(
  status: number,
  responseText: string,
): boolean {
  if (status === 409) {
    return true;
  }
  return extractResponseErrorMessages(responseText).some((line) => {
    const normalized = normalizeServerMessage(line);
    return (
      normalized.includes('already exists') ||
      normalized.includes('already in use') ||
      normalized.includes('user with this email or phone')
    );
  });
}

/** Full user-facing text when email, phone, or business ID conflicts with an existing account. */
export function formatRegisterUserAlreadyExistsMessage(
  t: TranslateFn,
  submitted: RegisterSubmittedContactIds,
  options?: { googleLockedEmail?: boolean; registerOption?: string },
): string {
  const isPrivateClient = options?.registerOption === 'private client';
  const lines = [
    t(
      isPrivateClient
        ? 'register.error.userAlreadyExistsPrivateClient'
        : 'register.error.userAlreadyExists',
    ),
  ];
  lines.push(
    t(
      isPrivateClient
        ? 'register.error.userAlreadyExistsSubmittedPrivateClient'
        : 'register.error.userAlreadyExistsSubmitted',
      {
        email: submitted.email || '—',
        phone: submitted.phone || '—',
        businessId: submitted.businessId || '—',
      },
    ),
  );
  if (options?.googleLockedEmail) {
    lines.push(t('register.error.userAlreadyExistsGoogleEmail'));
  }
  lines.push(
    t(
      isPrivateClient
        ? 'register.error.userAlreadyExistsTryLoginPrivateClient'
        : 'register.error.userAlreadyExistsTryLogin',
    ),
  );
  return lines.join('\n\n');
}

/** Registration 401/403 — server often returns generic JWT errors for blocked sign-ups. */
export type RegisterFailureContext = {
  businessWorkerPublicRegister?: boolean;
  businessWorkerSessionRegister?: boolean;
};

function formatRegisterAuthFailureMessage(
  status: number,
  t: TranslateFn,
  context?: RegisterFailureContext,
): string | null {
  if (status === 401 || status === 403) {
    if (context?.businessWorkerPublicRegister) {
      return t('register.error.businessWorkerPublicRejected');
    }
    if (context?.businessWorkerSessionRegister) {
      return t('register.error.businessWorkerSessionRejected');
    }
    return t('register.error.rejected');
  }
  return null;
}

/** Registration failure line with validation details when the API only returns “Bad Request”. */
export function formatRegisterHttpFailureMessage(
  status: number,
  responseText: string,
  t: TranslateFn,
  context?: RegisterFailureContext,
): string {
  const authFailure = formatRegisterAuthFailureMessage(status, t, context);
  if (authFailure) {
    return authFailure;
  }

  const extracted = extractResponseErrorMessages(responseText);
  const specific = extracted.filter((line) => !isGenericBadRequestMessage(line));

  if (specific.length > 0) {
    return joinLocalizedMessages(specific, t);
  }

  if (extracted.length > 0) {
    const joined = joinLocalizedMessages(extracted, t);
    if (!isGenericBadRequestMessage(joined)) {
      return joined;
    }
  }

  if (status === 400) {
    return t('register.error.badRequestWithStatus', { status: String(status) });
  }
  if (status === 409) {
    return t('register.error.conflictWithStatus', { status: String(status) });
  }
  if (status >= 500) {
    const joined = specific.length > 0 ? joinLocalizedMessages(specific, t) : '';
    if (/internal/i.test(joined)) {
      return t('register.error.serverInternalHint', { status: String(status) });
    }
    return t('register.error.serverWithStatus', { status: String(status) });
  }
  if (status >= 400) {
    return t('register.error.requestFailedWithStatus', { status: String(status) });
  }
  return t('register.error.requestFailed');
}
